/**
 * ArticulationEngine.js
 * Implements "Expression" enhancements for Contextual Articulation.
 * Determines Keyswitches and Performance styles based on musical context.
 */

import { UACC_MAP } from '../../data/InstrumentLibrary.js';

// BBCO Standard Keyswitches (matching InstrumentLibrary)
// Standard chromatic mapping starting C-2 (0)
const KEYS = {
    LONG: 0,
    SPICCATO: 1,
    PIZZ: 2,
    TREM: 3,
    TENUTO: 4,
    MARCATO: 5,
    COL_LEGNO: 6,
    HARMONICS: 7,
    SUL_TASTO: 8,
    CON_SORD: 9,
    FLAUTANDO: 10,
    TRILL_MAJ2: 11,
    TRILL_MIN2: 12
};

const ARTICULATION_CONFIG = {
    thresholds: {
        detach: {
            default: 0.25,
            action: 0.5,
            romantic: 0.15 // Short threshold for legato preference
        },
        marcato: {
            velocity: 0.86, // High velocity triggers marcato
            len: 1.0
        }
    }
};

/**
 * Analyzes a note event within its musical context to determine the best articulation.
 * @param {Object} event - The current note event {s, len, d, vel...}.
 * @param {Array} contextEvents - Array of surrounding events (for run detection).
 * @param {Object} instrument - The instrument definition.
 * @returns {Object} - { articulation: string, keyswitch: number }
 */
export const determineArticulation = (event, contextEvents, instrument) => {
    // 1. Default fallback based on duration
    let artic = (event.len < 0.2) ? 'spiccato' : 'long';
    let ks = (event.len < 0.2) ? KEYS.SPICCATO : KEYS.LONG;

    // Check Traits for Capabilities
    const canLegato = hasTrait(instrument, 'supports_legato', true) || (instrument.traits && instrument.traits.orchestral);
    const canSpiccato = hasTrait(instrument, 'agility', 'high') || (instrument.traits && instrument.traits.family === 'strings');

    // If instrument doesn't support complex KS, return simple mapping
    if (!instrument.keyswitches) return { articulation: artic, keyswitch: ks };

    // 2. Explicit Override (Logic Tags from Genre)
    // Only trust explicit override if it's NOT a generic default that might benefit from Context Analysis
    if (event.articulation && !['long', 'spiccato', 'short', 'staccato'].includes(event.articulation)) {
        artic = event.articulation;
        // Map string to KS
        if (artic === 'spiccato' || artic === 'staccato' || artic === 'short') ks = KEYS.SPICCATO;
        else if (artic === 'pizzicato' || artic === 'pluck') ks = KEYS.PIZZ;
        else if (artic === 'tremolo') ks = KEYS.TREM;
        else if (artic === 'marcato') ks = KEYS.MARCATO;
        else if (artic === 'tenuto') ks = KEYS.TENUTO;
        else if (artic === 'col_legno') ks = KEYS.COL_LEGNO;
        else if (artic === 'harmonics') ks = KEYS.HARMONICS;
        else if (artic === 'sul_tasto') ks = KEYS.SUL_TASTO;
        else if (artic === 'con_sord') ks = KEYS.CON_SORD;
        else if (artic === 'flautando') ks = KEYS.FLAUTANDO;
        else if (artic === 'trill' || artic === 'trill_maj2') ks = KEYS.TRILL_MAJ2;
        else if (artic === 'trill_min2') ks = KEYS.TRILL_MIN2;
        else ks = KEYS.LONG;
    }
    // 3. Contextual Analysis
    else {
        // A. Run Detection (Legato vs Spiccato)
        if (isLegatoContext(event, contextEvents)) {
            // Differentiation between Long (Sustain) and Legato
            artic = 'legato';
            // Use specific Legato keyswitch if available, otherwise fallback to Long
            if (instrument.keyswitches && instrument.keyswitches.LEGATO !== undefined) {
                ks = instrument.keyswitches.LEGATO;
            } else {
                ks = KEYS.LONG;
            }

            // Refine for Brass (Slurred vs Tongued)
            if (instrument.traits && instrument.traits.legatoType === 'slurred') {
                artic = 'legato_slurred';
            }
        }

        // B. Interval Awareness (Enhancement #5)
        // If leap is large (> 5 semitones) and note is short-ish, detach it.
        // Exception: Lyrical/Romantic lines often feature smooth large leaps.
        // Constraint: Only detach if the note is fast (< 0.25) OR if the style is specifically 'Action' or 'Rhythmic'.
        const isActionOrRhythmic = instrument.traits && (instrument.traits.role === 'rhythmic' || instrument.traits.style === 'action');
        const isLyrical = instrument.traits && (instrument.traits.style === 'romantic' || instrument.traits.style === 'lyrical');

        // Threshold: 0.25s (16th note at 120bpm) is usually detached for leaps.
        // 0.5s (8th note) can be legato in lyrical lines.
        let detachThreshold = ARTICULATION_CONFIG.thresholds.detach.default;
        if (isActionOrRhythmic) detachThreshold = ARTICULATION_CONFIG.thresholds.detach.action;
        if (isLyrical) detachThreshold = ARTICULATION_CONFIG.thresholds.detach.romantic;

        if (isLeapContext(event, contextEvents) && event.len < detachThreshold) {
             artic = 'spiccato'; // or Marcato
             ks = KEYS.SPICCATO;
        }

        // C. Marcato / Accent
        // Logic: If velocity > 110 (0.86), force MARCATO if it's a short/medium note.
        if (event.vel > ARTICULATION_CONFIG.thresholds.marcato.velocity && event.len < ARTICULATION_CONFIG.thresholds.marcato.len) {
            artic = 'marcato';
            ks = KEYS.MARCATO; // Use actual Marcato keyswitch
        }
        else if (event.vel > 0.85 && event.len < 0.4 && isOnBeat(event.s)) {
            artic = 'marcato';
            ks = KEYS.MARCATO;
        }
    }

    return { articulation: artic, keyswitch: ks };
};

const hasTrait = (inst, key, val) => inst.traits && inst.traits[key] === val;

// Helper: Check if note is part of a connected run
const isLegatoContext = (event, allEvents) => {
    if (!allEvents) return false;

    // Find neighbors
    const prev = allEvents.find(e => Math.abs((e.s + e.len) - event.s) < 0.05);
    const next = allEvents.find(e => Math.abs((event.s + event.len) - e.s) < 0.05);

    // Interval check for next note
    let smallInterval = true;
    if (next) {
        const diff = Math.abs(next.d - event.d);
        if (diff > 4) smallInterval = false; // Leap implies detached
    }

    // If connected to either side and short-ish, it's a run.
    if ((prev || next) && event.len < 0.6 && smallInterval) return true;

    return false;
};

// Helper: Check if note is a leap
const isLeapContext = (event, allEvents) => {
    if (!allEvents) return false;
    const next = allEvents.find(e => Math.abs((event.s + event.len) - e.s) < 0.05);
    if (next) {
        if (Math.abs(next.d - event.d) > 5) return true;
    }
    return false;
};

// Helper: Check if time is on a strong beat (Quarter note grid)
const isOnBeat = (time) => {
    return Math.abs(time % 1) < 0.05;
};

// --- Enhancement #5: Phrasing Taper ---
export const applyPhrasing = (events) => {
    if (!events || events.length === 0) return events;

    // Group by connected phrases
    // A phrase is a sequence of notes with no gaps > 0.1
    const phrases = [];
    let currentPhrase = [];

    events.sort((a,b) => a.s - b.s).forEach((ev, i) => {
        if (currentPhrase.length === 0) {
            currentPhrase.push(ev);
        } else {
            const last = currentPhrase[currentPhrase.length-1];
            if (ev.s - (last.s + last.len) < 0.2) {
                currentPhrase.push(ev);
            } else {
                phrases.push(currentPhrase);
                currentPhrase = [ev];
            }
        }
    });
    if (currentPhrase.length > 0) phrases.push(currentPhrase);

    // Process Phrases
    const processed = [];
    phrases.forEach(phrase => {
        if (phrase.length > 2) {
            // Taper the last note
            const last = phrase[phrase.length-1];

            // Clone to avoid mutating original if reused
            const newLast = { ...last, vel: (last.vel || 0.7) * 0.7 };

            // Swell the middle?
            // (Optional, kept simple for now)

            phrase[phrase.length-1] = newLast;
        }
        processed.push(...phrase);
    });

    return processed;
};

// --- Enhancement #5: Keyswitch Injection ---
/**
 * Injects keyswitch events based on note properties.
 * This is a post-processing step for the whole track.
 * @param {Array} events - The instrument's events
 * @param {Object} instrument - Instrument definition
 * @returns {Array} - Events with keyswitch properties attached
 */
export const applyKeyswitches = (events, instrument) => {
    if (!events || events.length === 0) return events;

    // Check for UACC support (explicit trait or default for certain categories if we wanted)
    const useUACC = (instrument.traits && instrument.traits.useUACC) || false;

    if (!instrument.keyswitches && !useUACC && instrument.category !== 'Orchestral') return events;

    // Use standard generic map if no specific map exists
    const KS = instrument.keyswitches || {
        long: 0,     // C-2
        spiccato: 1, // C#-2
        pizz: 2,     // D-2
        tremolo: 3   // D#-2
    };

    return events.map(ev => {
        let ksValue = undefined;

        // Refine Articulation Context (Smart Legato Detection)
        const { articulation: smartArtic } = determineArticulation(ev, events, instrument);
        const artic = smartArtic;

        if (useUACC) {
            // --- UACC LOGIC ---
            if (artic === 'legato' || artic === 'legato_slurred') ksValue = UACC_MAP.LEGATO;
            else if (artic === 'long' || artic === 'sustain') ksValue = UACC_MAP.LONG;
            else if (artic === 'spiccato' || artic === 'staccato' || artic === 'short') ksValue = UACC_MAP.SPICCATO;
            else if (artic === 'marcato') ksValue = UACC_MAP.MARCATO;
            else if (artic === 'pizzicato' || artic === 'pluck') ksValue = UACC_MAP.PIZZICATO;
            else if (artic === 'tremolo') ksValue = UACC_MAP.TREMOLO;
            else if (artic === 'trill') ksValue = UACC_MAP.TRILL;
            else if (artic === 'col_legno') ksValue = UACC_MAP.COL_LEGNO;
            else if (artic === 'harmonics') ksValue = UACC_MAP.HARMONICS;
            else if (artic === 'sul_tasto') ksValue = UACC_MAP.SUL_TASTO;
            else if (artic === 'con_sord') ksValue = UACC_MAP.CON_SORD;
            else if (artic === 'flautando') ksValue = UACC_MAP.FLAUTANDO;
            else {
                // Fallback UACC
                if (ev.len < 0.25) ksValue = UACC_MAP.SPICCATO;
                else ksValue = UACC_MAP.LONG;
            }
        }
        else {
            // --- STANDARD KEYSWITCH LOGIC ---
            if (artic === 'spiccato' || artic === 'staccato' || artic === 'short') {
                ksValue = KS.spiccato;
            } else if (artic === 'pizzicato') {
                ksValue = KS.pizz;
            } else if (artic === 'tremolo' || artic === 'trill') {
                ksValue = KS.tremolo;
            } else if (artic === 'legato' || artic === 'long' || artic === 'legato_slurred') {
                // Distinguish if KS map supports it, otherwise map to Long
                ksValue = (artic === 'legato' && KS.legato !== undefined) ? KS.legato : KS.long;
            } else if (artic === 'marcato') {
                ksValue = (KS.marcato !== undefined) ? KS.marcato : KS.long;
            }
            // 2. Derive from Duration (if no explicit meta)
            else {
                 if (ev.len < 0.25) ksValue = KS.spiccato; // 16th or less
                 else ksValue = KS.long;
            }
        }

        // Return event with keyswitch (and uacc flag if needed, though usually just the value on a CC is handled by the playback engine, here we are assigning 'keyswitch' property which presumably drives NoteOn or CC based on engine)
        // Assumption: The playback engine reads 'keyswitch' and checks if it's a Note Number.
        // If UACC is used, we might need to flag it so the engine knows to send CC32 instead of NoteOn.
        // For now, let's assume 'keyswitch' property holds the value.
        // If useUACC is true, we attach a `uacc: true` property to the event?
        // Or better, let the playback engine check the instrument traits.

        return { ...ev, keyswitch: ksValue, uacc: useUACC };
    });
};
